import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, CircularProgress, Autocomplete } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createPriceFormula, updatePriceFormula, type PriceFormula } from '../../api/preturi'
import { listArticles } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

const schema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  rawMaterialPricePerKg: z.string().min(1, 'Câmp obligatoriu'),
  aluWeightG: z.string().optional(),
  aluPricePerKg: z.string().optional(),
  conversionCostPer1000: z.string().min(1, 'Câmp obligatoriu'),
  marginPer1000: z.string().min(1, 'Câmp obligatoriu'),
  effectiveFrom: z.string().min(1, 'Câmp obligatoriu'),
  effectiveTo: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  articleId: '', rawMaterialPricePerKg: '', aluWeightG: '', aluPricePerKg: '',
  conversionCostPer1000: '', marginPer1000: '', effectiveFrom: '', effectiveTo: '',
}

function numOrNull(v: string | undefined): number | null {
  if (!v) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export default function PriceFormulaFormDialog({ formula, onClose }: { formula: PriceFormula | null | 'new'; onClose: () => void }) {
  const open = formula !== null
  const editing = formula !== null && formula !== 'new'
  const qc = useQueryClient()

  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (editing) {
      const f = formula as PriceFormula
      reset({
        articleId: f.articleId, rawMaterialPricePerKg: String(f.rawMaterialPricePerKg),
        aluWeightG: f.aluWeightG != null ? String(f.aluWeightG) : '', aluPricePerKg: f.aluPricePerKg != null ? String(f.aluPricePerKg) : '',
        conversionCostPer1000: String(f.conversionCostPer1000), marginPer1000: String(f.marginPer1000),
        effectiveFrom: f.effectiveFrom, effectiveTo: f.effectiveTo ?? '',
      })
    } else if (formula === 'new') {
      reset(EMPTY)
    }
  }, [formula, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = {
        articleId: values.articleId, rawMaterialPricePerKg: Number(values.rawMaterialPricePerKg),
        aluWeightG: numOrNull(values.aluWeightG), aluPricePerKg: numOrNull(values.aluPricePerKg),
        conversionCostPer1000: Number(values.conversionCostPer1000), marginPer1000: Number(values.marginPer1000),
        effectiveFrom: values.effectiveFrom, effectiveTo: values.effectiveTo || null,
      }
      if (editing) return updatePriceFormula((formula as PriceFormula).id, payload)
      return createPriceFormula(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'preturi', 'formule'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare formulă de preț' : 'Formulă de preț nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="articleId" control={control} render={({ field }) => (
              <Autocomplete
                options={articles?.map((a) => a.id) ?? []}
                getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.articleId} helperText={errors.articleId?.message} />}
              />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="rawMaterialPricePerKg" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Preț materie primă (EUR/kg)" fullWidth
                  error={!!errors.rawMaterialPricePerKg} helperText={errors.rawMaterialPricePerKg?.message} />
              )} />
              <Controller name="conversionCostPer1000" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Cost conversie / 1.000 buc" fullWidth
                  error={!!errors.conversionCostPer1000} helperText={errors.conversionCostPer1000?.message} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="aluWeightG" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Greutate Alu/buc (g, opțional)" fullWidth />
              )} />
              <Controller name="aluPricePerKg" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Preț Alu (EUR/kg, opțional)" fullWidth />
              )} />
            </Stack>
            <Controller name="marginPer1000" control={control} render={({ field }) => (
              <TextField {...field} type="number" label="Marjă / 1.000 buc" fullWidth
                error={!!errors.marginPer1000} helperText={errors.marginPer1000?.message} />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="effectiveFrom" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Valabil de la" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.effectiveFrom} helperText={errors.effectiveFrom?.message} />
              )} />
              <Controller name="effectiveTo" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Valabil până la" fullWidth slotProps={{ inputLabel: { shrink: true } }} />
              )} />
            </Stack>
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
